import React, { useState } from 'react';
import { Plus, Edit3, Trash2, MapPin, X, Check, AlertCircle, Navigation } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

export default function LinesMaster({ 
  centers = [], 
  lines = [], 
  onSaveLine, 
  onDeleteLine, 
  currentTenant, 
  lang = 'mr' 
}) {
  const isMr = lang === 'mr';
  const tenantSlug = currentTenant?.slug || (() => {
    try {
      const saved = localStorage.getItem('milkpro_tenant_data');
      return saved ? JSON.parse(saved)?.slug : 'samarth-krupa-dairy';
    } catch {
      return 'samarth-krupa-dairy';
    }
  })();

  const [selectedCenterId, setSelectedCenterId] = useState(centers[0] ? centers[0].id.toString() : '1');
  const [modalMode, setModalMode] = useState(null); // 'add' | 'edit'
  const [selectedLine, setSelectedLine] = useState(null);
  const [deleteConfirmLine, setDeleteConfirmLine] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [statusMsg, setStatusMsg] = useState(null);

  const initialForm = {
    center_id: selectedCenterId,
    code: '',
    name_mr: '',
    name_en: '',
    area: '',
    delivery_boy_name: '',
    delivery_boy_phone: '',
    is_active: true
  };
  const [formData, setFormData] = useState(initialForm);

  // Open Add Modal
  const handleOpenAdd = () => {
    setFormData({
      ...initialForm,
      center_id: selectedCenterId,
      code: 'LINE-0' + (lines.length + 1)
    });
    setSelectedLine(null);
    setModalMode('add');
  };

  // Open Edit Modal
  const handleOpenEdit = (l) => {
    setFormData({
      center_id: (l.center_id || l.centerId || selectedCenterId).toString(),
      code: l.code || '',
      name_mr: l.name_mr || l.name || '',
      name_en: l.name_en || l.nameEn || '',
      area: l.area || '',
      delivery_boy_name: l.delivery_boy_name || l.boyName || '',
      delivery_boy_phone: l.delivery_boy_phone || l.boyPhone || '',
      is_active: l.is_active !== undefined ? Boolean(l.is_active) : true
    });
    setSelectedLine(l);
    setModalMode('edit');
  };

  // Submit Add / Edit Form
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatusMsg(null);

    const payload = {
      center_id: parseInt(formData.center_id, 10) || 1,
      code: formData.code.trim().toUpperCase(),
      name_mr: formData.name_mr.trim(),
      name_en: formData.name_en.trim() || formData.name_mr.trim(),
      area: formData.area.trim(),
      delivery_boy_name: formData.delivery_boy_name.trim(),
      delivery_boy_phone: formData.delivery_boy_phone.trim(),
      is_active: formData.is_active
    };

    try {
      const url = modalMode === 'add'
        ? `${API_BASE}/tenant/${tenantSlug}/lines`
        : `${API_BASE}/tenant/${tenantSlug}/lines/${selectedLine.id}`;
      
      const method = modalMode === 'add' ? 'POST' : 'PUT';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();

      if (data.success) {
        setStatusMsg({ type: 'success', text: data.message });
        if (onSaveLine) {
          onSaveLine(data.line);
        }
        setModalMode(null);
      } else {
        setStatusMsg({ type: 'error', text: data.message || 'लाईन सेव्ह करता आली नाही.' });
      }
    } catch (err) {
      console.error('Error saving line:', err);
      setStatusMsg({ type: 'error', text: 'बॅकएंड सर्व्हर एरर: ' + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Line
  const handleDeleteLine = async () => {
    if (!deleteConfirmLine) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/lines/${deleteConfirmLine.id}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.success) {
        setStatusMsg({ type: 'success', text: data.message });
        if (onDeleteLine) {
          onDeleteLine(deleteConfirmLine.id);
        }
        setDeleteConfirmLine(null);
      } else {
        setStatusMsg({ type: 'error', text: data.message });
      }
    } catch (err) {
      console.error('Error deleting line:', err);
      setStatusMsg({ type: 'error', text: 'मार्ग डिलीट करता आला नाही: ' + err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const filteredLines = lines.filter(l => {
    const cId = (l.center_id || l.centerId || '').toString();
    return cId === selectedCenterId.toString() || selectedCenterId === 'ALL';
  });

  

  return (
    <div style={{ maxWidth: 1440, margin: '0 auto', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      
      {/* Toast Alert */}
      {statusMsg && (
        <div style={{
          padding: '0.75rem 1rem',
          borderRadius: 8,
          fontSize: '0.85rem',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: statusMsg.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: statusMsg.type === 'success' ? '#166534' : '#991b1b',
          border: `1px solid ${statusMsg.type === 'success' ? '#86efac' : '#fca5a5'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {statusMsg.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
            <span>{statusMsg.text}</span>
          </div>
          <button onClick={() => setStatusMsg(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={15} /></button>
        </div>
      )}

      {/* Header */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        padding: '1rem 1.25rem',
        border: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Navigation size={22} color="#0284c7" />
            <span>{isMr ? 'दूध वितरण मार्ग (Lines Master)' : 'Delivery Lines Master'}</span>
          </h2>
          <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
            {isMr ? 'केंद्रांनुसार वितरण मार्ग तयार करणे, डिलिव्हरी बॉय नेमणूक आणि परिसर व्यवस्थापन' : 'Manage delivery routes, assign delivery boys and coverage areas'}
          </div>
        </div>

        <button
          onClick={handleOpenAdd}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0.6rem 1.2rem',
            borderRadius: 8,
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            color: '#fff',
            fontWeight: 800,
            fontSize: '0.85rem',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 8px rgba(2, 132, 199, 0.25)'
          }}
        >
          <Plus size={16} />
          <span>{isMr ? '+ नवीन मार्ग जोडा' : '+ Add New Line'}</span>
        </button>
      </div>

      {/* Center Selector Dropdown */}
      <div style={{
        background: '#ffffff',
        padding: '0.85rem 1.25rem',
        borderRadius: 12,
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexWrap: 'wrap'
      }}>
        <span style={{ fontSize: '0.85rem', fontWeight: 800, color: '#334155' }}>
          {isMr ? 'शाखा / केंद्र निवडा:' : 'Select Center:'}
        </span>
        <select
          value={selectedCenterId}
          onChange={e => setSelectedCenterId(e.target.value)}
          style={{
            padding: '0.5rem 1rem',
            borderRadius: 8,
            border: '1px solid #cbd5e1',
            fontSize: '0.85rem',
            fontWeight: 700,
            minWidth: 260,
            background: '#fff'
          }}
        >
          <option value="ALL">{isMr ? 'सर्व केंद्रे (All Centers)' : 'All Centers'}</option>
          {centers.map(c => (
            <option key={c.id} value={c.id.toString()}>{c.name_mr || c.name || c.nameEn}</option>
          ))}
        </select>
        <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
          ({filteredLines.length} {isMr ? 'मार्ग सक्रिय' : 'routes active'})
        </span>
      </div>

      {/* Lines Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '1rem' }}>
        {filteredLines.map(l => {
          const lineName = l.name_mr || l.name;
          const boyName = l.delivery_boy_name || l.boyName || (isMr ? 'नेमलेला नाही' : 'Unassigned');
          const boyPhone = l.delivery_boy_phone || l.boyPhone || '-';

          return (
            <div key={l.id} style={{
              background: '#ffffff',
              borderRadius: 14,
              padding: '1.25rem',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 5px rgba(0,0,0,0.03)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{
                    fontSize: '0.72rem',
                    fontWeight: 800,
                    background: '#f0fdf4',
                    color: '#166534',
                    border: '1px solid #bbf7d0',
                    padding: '2px 8px',
                    borderRadius: 6
                  }}>
                    {l.code}
                  </span>

                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => handleOpenEdit(l)}
                      title={isMr ? "बदल करा" : "Edit"}
                      style={{
                        background: '#eff6ff',
                        border: '1px solid #bfdbfe',
                        color: '#1d4ed8',
                        borderRadius: 6,
                        padding: '4px 8px',
                        cursor: 'pointer'
                      }}
                    >
                      <Edit3 size={13} />
                    </button>

                    <button
                      onClick={() => setDeleteConfirmLine(l)}
                      title={isMr ? "हटवा" : "Delete"}
                      style={{
                        background: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#dc2626',
                        borderRadius: 6,
                        padding: '4px 8px',
                        cursor: 'pointer'
                      }}
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>

                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#0f172a', margin: '0.75rem 0 0.35rem 0' }}>
                  {lineName}
                </h3>

                <div style={{ fontSize: '0.8rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 5, marginTop: 4 }}>
                  <MapPin size={14} color="#0284c7" />
                  <span>{l.area || (isMr ? 'परिसर नमूद नाही' : 'No area specified')}</span>
                </div>
              </div>

              <div style={{
                borderTop: '1px solid #f1f5f9',
                marginTop: '1rem',
                paddingTop: '0.75rem',
                fontSize: '0.8rem',
                color: '#334155',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '0.68rem', color: '#64748b' }}>{isMr ? 'डिलिव्हरी वितरक:' : 'Delivery Boy:'}</div>
                  <strong style={{ color: '#0f172a' }}>{boyName}</strong>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.68rem', color: '#64748b' }}>{isMr ? 'मोबाईल:' : 'Phone:'}</div>
                  <span style={{ fontWeight: 700, color: '#0369a1' }}>{boyPhone}</span>
                </div>
              </div>

            </div>
          );
        })}
      </div>

      {/* ADD / EDIT LINE MODAL FORM */}
      {modalMode && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 16,
            maxWidth: 480,
            width: '100%',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}>
            <div style={{
              background: '#0284c7',
              color: '#fff',
              padding: '1rem 1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <span style={{ fontWeight: 800, fontSize: '1.05rem' }}>
                  {modalMode === 'add' 
                    ? (isMr ? 'नवीन वितरण मार्ग नोंदणी' : 'Add New Delivery Line')
                    : (isMr ? 'वितरण मार्गामध्ये बदल करा' : 'Edit Delivery Line')}
                </span>
                <div style={{ fontSize: '0.75rem', color: '#e0f2fe' }}>
                  {isMr ? 'मार्ग कोड, नाव व वितरक माहिती' : 'Enter route code, name and delivery boy'}
                </div>
              </div>
              <button onClick={() => setModalMode(null)} style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer' }}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmitForm} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
              
              {/* Center Selection */}
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  {isMr ? 'संबंधित केंद्र / शाखा *' : 'Branch Center *'}
                </label>
                <select
                  value={formData.center_id}
                  onChange={e => setFormData({ ...formData, center_id: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', background: '#fff', boxSizing: 'border-box' }}
                >
                  {centers.map(c => (
                    <option key={c.id} value={c.id.toString()}>{c.name_mr || c.name || c.nameEn}</option>
                  ))}
                </select>
              </div>

              {/* Code & Line Name Marathi */}
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'मार्ग कोड *' : 'Route Code *'}
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="LINE-01"
                    value={formData.code}
                    onChange={e => setFormData({ ...formData, code: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'मार्गाचे नाव (मराठीत) *' : 'Line Name (Marathi) *'}
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="उदा. मार्ग क्र. ४ (बाणेर - बालेवाडी)"
                    value={formData.name_mr}
                    onChange={e => setFormData({ ...formData, name_mr: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* Area */}
              <div>
                <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                  {isMr ? 'परिसर / कव्हरेज एरिया (Area / Locality)' : 'Area / Locality'}
                </label>
                <input
                  type="text"
                  placeholder="उदा. हाय स्ट्रीट, पॅनकार्ड क्लब रोड, बाणेर"
                  value={formData.area}
                  onChange={e => setFormData({ ...formData, area: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                />
              </div>

              {/* Delivery Boy Name & Phone */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'डिलिव्हरी वितरक नाव' : 'Delivery Boy Name'}
                  </label>
                  <input
                    type="text"
                    placeholder="उदा. अजय शिंदे"
                    value={formData.delivery_boy_name}
                    onChange={e => setFormData({ ...formData, delivery_boy_name: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: 700, color: '#334155', marginBottom: 4 }}>
                    {isMr ? 'मोबाईल नंबर' : 'Phone Number'}
                  </label>
                  <input
                    type="tel"
                    placeholder="९८२२xxxxxx"
                    value={formData.delivery_boy_phone}
                    onChange={e => setFormData({ ...formData, delivery_boy_phone: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.65rem', borderTop: '1px solid #e2e8f0', paddingTop: '1rem', marginTop: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setModalMode(null)}
                  style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.6rem 1.1rem', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
                >
                  {isMr ? 'रद्द करा' : 'Cancel'}
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: submitting ? '#94a3b8' : '#0284c7',
                    color: '#fff',
                    border: 'none',
                    padding: '0.6rem 1.3rem',
                    borderRadius: 8,
                    fontWeight: 800,
                    cursor: submitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  {submitting ? (isMr ? 'सेव्ह होत आहे...' : 'Saving...') : (isMr ? 'मार्ग सेव्ह करा' : 'Save Line')}
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {deleteConfirmLine && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.6)',
          backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 100, padding: '1rem'
        }}>
          <div style={{ background: '#ffffff', borderRadius: 14, maxWidth: 420, width: '100%', overflow: 'hidden', padding: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#dc2626', marginBottom: '0.75rem' }}>
              <AlertCircle size={24} />
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800 }}>
                {isMr ? 'वितरण मार्ग डिलीट करायचा का?' : 'Delete Line?'}
              </h3>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.5, margin: '0 0 1rem 0' }}>
              {isMr
                ? `तुम्ही खरोखर "${deleteConfirmLine.name_mr || deleteConfirmLine.name}" हा मार्ग काढून टाकू इच्छिता?`
                : `Are you sure you want to delete "${deleteConfirmLine.name_mr || deleteConfirmLine.name}"?`}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.65rem' }}>
              <button
                type="button"
                onClick={() => setDeleteConfirmLine(null)}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '0.55rem 1rem', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}
              >
                {isMr ? 'रद्द करा' : 'Cancel'}
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleDeleteLine}
                style={{ background: '#dc2626', color: '#fff', border: 'none', padding: '0.55rem 1.1rem', borderRadius: 8, fontWeight: 800, cursor: 'pointer' }}
              >
                {submitting ? (isMr ? 'काढत आहे...' : 'Deleting...') : (isMr ? 'होय, डिलीट करा' : 'Yes, Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
